import styles from './style.module.scss'
import { PageTitle, Table, BackButton, ActionsDropdown } from '@Root/components'
import { useParams } from 'react-router-dom'
import { API } from '@Root/API'
import { useDispatch } from 'react-redux'
import { snackbarActions } from '@Root/store'
import { errorMessage } from '@Root/helpers'
import { useState, useCallback } from 'react'
import { useHistory } from 'react-router-dom'

export const DetailsCognitoForms = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { id } = useParams()
  const [shouldBeUpdated, setShouldBeUpdated] = useState(false)
  const [checkedIds, setCheckedIds] = useState([])
  const [isResyncing, setIsResyncing] = useState(false)

  const columns = [
    {
      name: 'ID',
      field: 'id',
      searchInputType: 'textInput',
      cellType: 'link',
      cellLinkField: 'id_link',
    },
    {
      name: 'Cognito Entry ID',
      field: 'cognito_entry_id',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Form Name',
      field: 'form_name',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Status',
      field: 'status_label',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Contact Email',
      field: 'contact_email',
      searchInputType: 'textInput',
      cellType: 'mailLink',
    },
    {
      name: 'Contact Name',
      field: 'contact_name',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Created At',
      field: 'created_at',
      searchInputType: 'datePicker',
      cellType: 'text',
    },
    {
      name: 'Updated At',
      field: 'updated_at',
      searchInputType: 'datePicker',
      cellType: 'text',
    },
  ]

  const fetchDataHandler = async params => {
    try {
      const response = await API.cognitoForms.getAllSubmissions(id)
      const data = response.data || response
      const meta = response.meta || data.meta

      if (data.data && Array.isArray(data.data)) {
        const processedData = data.data.map(row => ({
          ...row,
          parsed_data: row.parsed_data ? JSON.stringify(row.parsed_data, null, 2) : '',
        }))

        return {
          data: {
            data: processedData || [],
            meta: meta || {
              from: 1,
              to: processedData?.length || 0,
              per_page: processedData?.length || 0,
              last_page: 1,
              total: processedData?.length || 0,
            },
            columns,
          },
        }
      }
    } catch (error) {
      console.log(error)
      throw error
    }
  }

  const onError = error => {
    dispatch(snackbarActions.setSnackbar({ text: errorMessage(error), isError: true }))
  }

  const onSuccess = message => {
    dispatch(snackbarActions.setSuccessNotification({ text: message }))
  }

  const refreshTable = useCallback(() => {
    setShouldBeUpdated(true)
    setTimeout(() => setShouldBeUpdated(false), 100)
  }, [])

  const handleResyncSelected = async () => {
    if (!checkedIds.length || isResyncing) return
    setIsResyncing(true)
    let successCount = 0
    let failCount = 0

    for (const submissionId of checkedIds) {
      try {
        const res = await API.cognitoForms.resyncSubmission(submissionId)
        const result = res.data || res
        if (result.success) {
          successCount++
        } else {
          failCount++
        }
      } catch {
        failCount++
      }
    }

    setIsResyncing(false)
    setCheckedIds([])

    if (failCount > 0) {
      onSuccess(`Resync completed: ${successCount} synced, ${failCount} failed`)
    } else {
      onSuccess(`Resync completed: ${successCount} submissions synced`)
    }

    refreshTable()
  }

  const actions = [
    {
      name: isResyncing ? 'Resyncing...' : `Resync Selected (${checkedIds.length})`,
      handler: handleResyncSelected,
      disabled: !checkedIds.length || isResyncing,
    },
  ]

  const onChooseAction = actionName => {
    const action = actions.find(a => a.name === actionName)
    if (action?.handler) action.handler()
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.titleWrap}>
        <BackButton text={'Forms'} clickHandler={() => history.push('/home/forms')} />
        <PageTitle title={'Submissions'} />
      </div>

      <div className={styles.actionsDropdownWrapper}>
        <ActionsDropdown
          actions={actions}
          clickHandler={onChooseAction}
        />
      </div>

      <Table
        key={id}
        name='cognito-submissions'
        columns={columns}
        fetchDataHandler={() => {
          return fetchDataHandler()
        }}
        fetchColumnOptions={() => API.getColumnOptions('cognito-submissions')}
        fetchSaveColumnOptions={options => API.saveColumnOptions('cognito-submissions', options)}
        hasSorting={false}
        hasFilters={false}
        errorHandler={onError}
        successHandler={onSuccess}
        shouldBeUpdated={shouldBeUpdated}
        onCheckedRowsChange={setCheckedIds}
        styleConfig={{
          datePeriodDropdownStyle: {
            display: 'none',
          },
        }}
      />
    </div>
  )
}
